import { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { List, ListItem, ListItemIcon, ListItemText, Typography, Collapse, Hidden, Paper, Divider } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { NavigationType } from '../types'
import { Link as RouterLink } from 'react-router-dom'

import NavBarAccordionNestedItem from './nav-bar-accordion-nested-Item'

// Icons
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { themeV5 } from 'theme/v5'

const NavBarAccordion = ({ title, className, navigation, openMobile, onMobileClose, openMostRecentOnLoad, ...rest }) => {

  const location = useLocation()


  const fbtSubmissionsActiveFilter = useSelector(({ fbtSubmissions }) => fbtSubmissions.filtered)
  const fbtSummaryActiveFilter = useSelector(({ fbtSummary }) => fbtSummary.filtered)

  const isFbtSubmissionPage = window.location.href.includes('/fbt/submissions')
  const isFbtSummaryPage = window.location.href.includes('/fbt/summary')

  const isSelected = (path) => {
    if (isFbtSubmissionPage || isFbtSummaryPage) { // for fbt reports only
      return isFbtSummaryPage
        ? path.includes(fbtSummaryActiveFilter.quarter) && path.includes(fbtSummaryActiveFilter.year)
        : path.includes(fbtSubmissionsActiveFilter.month) && path.includes(fbtSubmissionsActiveFilter.year)
    }
    return window.location.href.includes(path)

  }

  useEffect(() => {
    if (openMobile) {
      onMobileClose && onMobileClose()
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <Fragment>
      <Hidden xsDown>
        <Paper {...rest}
          sx={{
            paddingTop: 3,
            maxHeight: 'max(100%, 100vh)',
            overflowY: 'auto',
            ...className
          }}
          elevation={1} square>
          {title && (
            <Typography
              marginLeft={2}
              variant="overline"
              sx={{
                color: themeV5.palette.icons,
              }}>
              {title}
            </Typography>
          )}
          <List>
            {navigation.map(({ title, link, Icon, nestedItems, disabled }, index) => (
              nestedItems ? (<div key={`nav-bar-accordion-level-1-item-${index}`}>
                <Divider
                  sx={{
                    margin: '0px 16px'
                  }}/>
                <NavBarAccordionNestedItems
                  key={`accordion-navbar-item-${index}`}
                  title={title} // level 1
                  link={link}
                  Icon={Icon}
                  nestedItems={nestedItems}
                  disabled={disabled}
                  isSelected={isSelected}
                  openMostRecentOnLoad={openMostRecentOnLoad}/>
              </div>) : (<div key={`nav-bar-accordion-level-1-item-${index}`}>
                {navigation.length - 1 === index && (
                  <Divider
                    sx={{
                      margin: '0px 16px 8px 16px'
                    }}/>
                )}
                <ListItem
                  button to={link} key={index} component={RouterLink}
                  sx={{
                    minHeight: 44,
                    padding: '5px 0 5px 15px',
                    '&:hover': { backgroundColor: 'transparent' },
                    ...(location.pathname === link && {
                      color: '#ced4da',
                    }),
                  }}>
                  {Icon && (
                    <ListItemIcon>
                      <Icon
                        sx={{
                          ...(isSelected(link) && {
                            color: '#ced4da',
                            width: '18px',
                            height: '18px',
                          })
                        }} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    id={`nav-item-${title}`}
                    sx={{
                      margin: '-1px 0',
                      fontWeight: 400,
                      color: 'rgba(233,236,239,.5)',
                      '&:hover': {
                        color: '#ced4da',
                      },
                      '& span, & svg': {
                        fontSize: '14px',
                      }
                    }}
                    primary={<Typography type="body1"
                      sx={{
                        fontSize: '14px',
                        letterSpacing: '0',
                        lineHeight: '19px',
                        '&:hover': { opacity: '0.8' },
                        ...(isSelected(link) && {
                          color: '#ced4da',
                        })
                      }}>
                      {title}
                    </Typography>}/>
                </ListItem>
              </div>)
            ))}
            <Divider
              sx={{
                margin: '0x 16px'
              }}/>
          </List>
        </Paper>
      </Hidden>
    </Fragment>
  )
}

NavBarAccordion.propTypes = {
  className: PropTypes.string,
  navigation: NavigationType,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  openMostRecentOnLoad: PropTypes.bool,
}

export default NavBarAccordion


// level 2 Nav items
const NavBarAccordionNestedItems = ({ title, link, Icon, nestedItems, disabled, isSelected, openMostRecentOnLoad }) => {

  const location = useLocation()

  const [openNested, setOpenNested] = useState(true)

  return (
    <>
      <ListItem button={!disabled} onClick={() => setOpenNested(!openNested)}
        sx={{
          minHeight: 44,
          padding: '5px 0 5px 15px',
          '&:hover': { backgroundColor: 'transparent' },
        }}>
        {Icon && (
          <ListItemIcon>
            <Icon />
          </ListItemIcon>
        )}
        <ListItemText
          sx={{
            margin: '-1px 0',
            fontWeight: 400,
            color: 'rgba(233,236,239,.5)',
            '&:hover': {
              color: '#ced4da',
            },
            '& span, & svg': {
              fontSize: '14px',
            }
          }}
          primary={<Typography
            sx={{
              color: '#263238',
              fontSize: '14px',
              letterSpacing: '0',
              lineHeight: '19px',
              '&:hover': { opacity: '0.8' },
              opacity: disabled ? '0.3' : 1
            }}>
            {title}
          </Typography>}/>
        {nestedItems && nestedItems.length > 0 && (
          <div style={{ marginRight: 20, marginTop: 5 }}>
            {openNested ? <ExpandLess /> : <ExpandMore />}
          </div>
        )}
      </ListItem>

      <Collapse in={openNested} timeout="auto" unmountOnExit>
        <List component="div" disablePadding style={{ paddingLeft: 30 }}>
          {nestedItems.map((nestedItem, nestedIndex) => (

            nestedItem?.nestedItems ? (
              <div key={`nav-bar-accordion-level-2-items-${nestedIndex}`}>
                <NavBarAccordionNestedItem
                  index={nestedIndex}
                  title={nestedItem?.title}
                  Icon={nestedItem?.Icon}
                  nestedItems={nestedItem?.nestedItems}
                  isSelected={isSelected}
                  openMostRecentOnLoad={openMostRecentOnLoad}/>
              </div>
            ) : (
              <ListItem
                key={`nav-bar-accordion-level-2-items-${nestedIndex}`}
                sx={{
                  minHeight: 44,
                  padding: '5px 0 5px 15px',
                  '&:hover': { backgroundColor: 'transparent' },
                  marginBottom: nestedItems.length - 1 === nestedIndex ? 15 : 0,
                  ...(location.pathname === nestedItem?.link && {
                    color: '#ced4da',
                  }),
                }}
                button component={RouterLink} to={nestedItem?.link}>
                {Icon && (
                  <ListItemIcon>
                    <Icon
                      sx={{
                        ...(isSelected(nestedItem?.link) && {
                          color: '#ced4da',
                          width: '18px',
                          height: '18px',
                        }),
                      }} />
                  </ListItemIcon>
                )}
                <ListItemText
                  id={`nav-item-${nestedItem?.title}`}
                  sx={{
                    margin: '-1px 0',
                    fontWeight: 400,
                    color: 'rgba(233,236,239,.5)',
                    '&:hover': {
                      color: '#ced4da',
                    },
                    '& span, & svg': {
                      fontSize: '14px',
                    }
                  }}
                  primary={<Typography type="body1"
                    sx={{
                      fontSize: '14px',
                      letterSpacing: '0',
                      lineHeight: '19px',
                      '&:hover': { opacity: '0.8' },
                      ...(isSelected(nestedItem?.link) && {
                        color: '#ced4da',
                      }),
                    }}>
                    {nestedItem?.title}
                  </Typography>}/>
              </ListItem>
            )
          ))}
        </List>
      </Collapse>
    </>
  )
}
